import { Link } from "react-router-dom";
import { Button } from "@/components/primitives";
import { Lightbulb } from "lucide-react";
import { DESCRIPTION_PREVIEW_LENGTH } from "../constants";
import type { AnalysisData } from "../types";
import { useMemo, useState } from "react";

import type { Reflection } from "../types";

interface SuggestedReflectionProps {
	analysisData: AnalysisData;
	reflections?: Reflection[];
}

export function SuggestedReflection({
	analysisData,
	reflections = [],
}: SuggestedReflectionProps) {
	const suggestions = useMemo(() => {
		const list =
			analysisData.reflectionSuggestions &&
			analysisData.reflectionSuggestions.length > 0
				? analysisData.reflectionSuggestions
				: analysisData.reflectionSuggestion
					? [analysisData.reflectionSuggestion]
					: [];
		// filter out suggestions similar to existing active reflections
		const isSimilar = (title: string) => {
			const words = title
				.toLowerCase()
				.split(/\s+/)
				.filter(
					(w) =>
						w.length > 3 &&
						![
							"with",
							"and",
							"the",
							"for",
							"from",
							"about",
							"that",
							"this",
						].includes(w),
				);
			return reflections.some((r) => {
				const rw = r.title.toLowerCase().split(/\s+/);
				const match = words.filter((w) =>
					rw.some((x) => x.includes(w) || w.includes(x)),
				).length;
				return match / Math.max(words.length, 1) > 0.3;
			});
		};
		return list.filter((s) => !isSimilar(s.title)).slice(0, 3);
	}, [analysisData, reflections]);

	const [idx, setIdx] = useState(0);
	if (suggestions.length === 0) return null;

	const current = suggestions[idx] || suggestions[0];
	const { title, description, reasoning } = current;
	const truncatedDescription =
		description.length > DESCRIPTION_PREVIEW_LENGTH
			? description.substring(0, DESCRIPTION_PREVIEW_LENGTH) + "..."
			: description;

	const canPrev = idx > 0;
	const canNext = idx < suggestions.length - 1;

	return (
		<div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
			<div className="border rounded-lg p-4 bg-[#fcfcfc] dark:bg-amber-950/20 border-gray-200 dark:border-gray-700 text-left">
				<div className="flex items-start justify-between mb-3">
					<div className="flex items-start space-x-3">
						<Lightbulb className="h-5 w-5 mt-0.5 text-primary" />
						<div className="flex-1">
							<h4 className="font-semibold text-foreground mb-1">
								Suggested New Reflection{suggestions.length > 1 ? "s" : ""}
							</h4>
							<p className="text-xs text-muted-foreground mb-3">
								Based on your recent check-ins, we've identified{" "}
								{suggestions.length > 1
									? `${suggestions.length} options`
									: "an option"}
							</p>
						</div>
					</div>
					{suggestions.length > 1 && (
						<div className="text-xs text-muted-foreground">
							{idx + 1} / {suggestions.length}
						</div>
					)}
				</div>

				<div className="mb-4">
					<h5 className="font-medium text-base mb-2 text-foreground">
						{title}
					</h5>
					<p className="text-sm text-muted-foreground mb-3 leading-relaxed">
						{truncatedDescription}
					</p>
					{reasoning && (
						<p className="text-xs text-muted-foreground">{reasoning}</p>
					)}
				</div>

				{suggestions.length > 1 && (
					<div className="flex items-center justify-between mb-3">
						<button
							type="button"
							className={`text-xs underline ${!canPrev ? "opacity-40 cursor-not-allowed" : ""}`}
							disabled={!canPrev}
							onClick={() => setIdx((i) => Math.max(0, i - 1))}
						>
							Previous
						</button>
						<button
							type="button"
							className={`text-xs underline ${!canNext ? "opacity-40 cursor-not-allowed" : ""}`}
							disabled={!canNext}
							onClick={() =>
								setIdx((i) => Math.min(suggestions.length - 1, i + 1))
							}
						>
							Next
						</button>
					</div>
				)}

				<Link
					to={`/reflection/new?suggested=true&tab=checkins&title=${encodeURIComponent(title)}&description=${encodeURIComponent(description)}&reasoning=${encodeURIComponent(reasoning || "")}`}
				>
					<Button size="sm" className="w-full">
						<Lightbulb className="h-4 w-4 mr-2" />
						Start This Reflection
					</Button>
				</Link>
			</div>
		</div>
	);
}
